import { Table, Space, Button, type TableColumnsType, type TableProps } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ExportExcelButton, ColumnManagerDropdown } from '../../common';
import { useColumnManager } from '../../../hooks';
import * as XLSX from 'xlsx';

const PaginatedTableLotes = ({ csvName, getTableData, getNroCliente }: any) => {

    // Fetch Data
    const [fullTableData, setFullTableData] = useState<any[]>([]);
    const [filteredTableData, setFilteredTableData] = useState<any[]>(fullTableData)
    const [loading, setLoading] = useState(false)

    const {user, getAccessTokenSilently} = useAuth0();

    const id_empresa = user?.id_empresa
    const id_cliente = user?.id_cliente
    const tipo_usuario = user?.tipo_usuario 
  
    const variables_api = `${tipo_usuario}/${id_empresa}/${id_cliente}`

    // Para filtrar
    const desired_output = (data: any, column: any) => {
        const unique_values = data
            .map((item: any) => item[column])
            .filter(
                (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
            );
        return unique_values;
    };

    // Para cuando se filtre la tabla desde la tabla.
    // En lugar de modificar la tabla completa, usamos otra variable.
    const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra: any) => {
        setFilteredTableData(extra.currentDataSource);
        getNroCliente(filters.nro_cliente)
      };

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          const apiEndpoint = `${process.env.REACT_APP_REPORTES_URL}/lotes/${variables_api}`;
          const token = await getAccessTokenSilently();
          const response = await fetch(apiEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const data = await response.json();
          // Compartimos y guardamos info.
          setFullTableData(Array.isArray(data) ? data : []);
          getTableData(Array.isArray(data) ? data : []);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching data:', error);
          setLoading(false);
        }
      };
      fetchData();
    }, [getAccessTokenSilently]);

    // Clientes únicos para el filtro.
    const clientesUnicos = desired_output(fullTableData, 'cliente')
                                .map((cliente: any) => ({text: cliente, value: cliente}));

    // Table
    type DataType = {
        cliente : string
        lote : number
        cantidad : number
        promedio_calidad : number
        promedio_microner : number
        promedio_hebra : number
        promedio_resistencia : number
        desde_calidad : number
        hasta_calidad : number
        desde_microner : number
        hasta_microner : number
        desde_hebra : number
        hasta_hebra : number
        desde_resistencia : number
        hasta_resistencia : number
      }
      
    const allColumns: TableColumnsType<DataType> = [
      {
        title: 'Cliente',
        dataIndex: 'cliente',
        key: 'cliente',
        width: 150,
        ellipsis: true,
        render: (text) => <i>{text}</i>,
  
        filters: clientesUnicos,
        onFilter: (value, record) => record.cliente.includes(value as string),
      },
      {
        title: 'Lote',
        dataIndex: 'lote',
        key: 'lote',
        width: 80,
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.lote - b.lote,
      },
      {
        title: 'Cantidad',
        dataIndex: 'cantidad',
        key: 'cantidad',
        width: 100,
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.cantidad - b.cantidad,
      },
      {
        title: 'Cal. Prom.',
        dataIndex: 'promedio_calidad',
        key: 'promedio_calidad',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.promedio_calidad - b.promedio_calidad,
      },
      {
        title: 'Mic. Prom.',
        dataIndex: 'promedio_microner',
        key: 'promedio_microner',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.promedio_microner - b.promedio_microner,
      },
      {
        title: 'Heb. Prom.',
        dataIndex: 'promedio_hebra',
        key: 'promedio_hebra',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.promedio_hebra - b.promedio_hebra,
      },
      {
        title: 'Resist. Prom.',
        dataIndex: 'promedio_resistencia',
        key: 'promedio_resistencia',
        width: 110,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.promedio_resistencia - b.promedio_resistencia,
      },
      {
        title: 'Desde Cal.',
        dataIndex: 'desde_calidad',
        key: 'desde_calidad',
        width: 100,
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.desde_calidad - b.desde_calidad,
      },
      {
        title: 'Hasta Cal.',
        dataIndex: 'hasta_calidad',
        key: 'hasta_calidad',
        width: 100,
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.hasta_calidad - b.hasta_calidad,
      },
      {
        title: 'Desde Mic.',
        dataIndex: 'desde_microner',
        key: 'desde_microner',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.desde_microner - b.desde_microner,
      },
      {
        title: 'Hasta Mic.',
        dataIndex: 'hasta_microner',
        key: 'hasta_microner',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.hasta_microner - b.hasta_microner,
      },
      {
        title: 'Desde Heb.',
        dataIndex: 'desde_hebra',
        key: 'desde_hebra',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.desde_hebra - b.desde_hebra,
      },
      {
        title: 'Hasta Heb.',
        dataIndex: 'hasta_hebra',
        key: 'hasta_hebra',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.hasta_hebra - b.hasta_hebra,
      },
      {
        title: 'Desde Resist.',
        dataIndex: 'desde_resistencia',
        key: 'desde_resistencia',
        width: 110,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.desde_resistencia - b.desde_resistencia,
      },
      {
        title: 'Hasta Resist.',
        dataIndex: 'hasta_resistencia',
        key: 'hasta_resistencia',
        width: 110,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.hasta_resistencia - b.hasta_resistencia,
      }
    ]

    // Lista de todas las columnas disponibles (keys)
    const allColumnKeys = allColumns.map(col => col.key as string).filter(Boolean);

    // Nombres amigables para las columnas
    const columnNames: Record<string, string> = {
        'cliente': 'Cliente',
        'lote': 'Lote',
        'cantidad': 'Cantidad',
        'promedio_calidad': 'Cal. Prom.',
        'promedio_microner': 'Mic. Prom.',
        'promedio_hebra': 'Heb. Prom.',
        'promedio_resistencia': 'Resist. Prom.',
        'desde_calidad': 'Desde Cal.',
        'hasta_calidad': 'Hasta Cal.',
        'desde_microner': 'Desde Mic.',
        'hasta_microner': 'Hasta Mic.',
        'desde_hebra': 'Desde Heb.',
        'hasta_hebra': 'Hasta Heb.',
        'desde_resistencia': 'Desde Resist.',
        'hasta_resistencia': 'Hasta Resist.'
    };

    // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
    const columnManager = useColumnManager({
        storageKey: 'lotes',
        allKeys: allColumnKeys,
    });

    // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
    const filteredColumns = useMemo(() => {
        const columnsByKey = new Map(allColumns.map(col => [col.key as string, col]));
        return columnManager.orderedVisibleKeys
            .map(key => columnsByKey.get(key))
            .filter((col): col is (typeof allColumns)[number] => Boolean(col));
    }, [columnManager.orderedVisibleKeys, allColumns]);

    const columns: TableColumnsType<DataType> = filteredColumns;

    const data: DataType[] = fullTableData.map((fila: any) => {
        return {
          cliente : fila.cliente,
          lote : fila.lote,
          cantidad : fila.cantidad,
          promedio_calidad : fila.promedio_calidad,
          promedio_microner : fila.promedio_microner,
          promedio_hebra : fila.promedio_hebra,
          promedio_resistencia : fila.promedio_resistencia,
          desde_calidad : fila.desde_calidad,
          hasta_calidad : fila.hasta_calidad,
          desde_microner : fila.desde_microner,
          hasta_microner : fila.hasta_microner,
          desde_hebra : fila.desde_hebra,
          hasta_hebra : fila.hasta_hebra,
          desde_resistencia : fila.desde_resistencia,
          hasta_resistencia : fila.hasta_resistencia,
        }
    });

    // Función para exportar a Excel
    const handleExportExcel = () => {
        try {
            const sourceData: any[] = filteredTableData.length > 0 ? filteredTableData : data;
            const exportData = sourceData.map((row: any) => {
                const exportRow: Record<string, any> = {};
                columnManager.orderedVisibleKeys.forEach(key => {
                    exportRow[columnNames[key] || key] = row[key];
                });
                return exportRow;
            });
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Lotes');
            XLSX.writeFile(workbook, csvName || `lotes_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
        }
    };

    return (
      <div>
        <div style={{ 
            marginBottom: '16px', 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap'
        }}>
            <ColumnManagerDropdown
                manager={columnManager}
                labels={columnNames}
                buttonSize="middle"
            />
            <ExportExcelButton
                onClick={handleExportExcel}
                size="middle"
                disabled={fullTableData.length === 0}
            />
        </div>
        <Table 
            columns={columns} 
            dataSource={data}
            bordered
            size="middle"
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 400px))' }}
            onChange={onChange}
            loading={loading}
            pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomRight']
            }}
            >
        </Table>
      </div>
    );
  };
  
  export default PaginatedTableLotes;